<template>
  <v-card width="600">
    <v-form ref="pathForm" v-model="isValidPathForm">
      <v-layout pa-2>
        <div>
          <v-flex layout pa-3>
            <v-img v-if="isReadOnly" :src="image.url" class="missionImage" />
            <image-input
              v-else
              :label="$t('pathForm.picture') + ' *'"
              :showHint="showHint"
              :default="image"
              :langEdition="langEdition"
              @change-image-input="updateImage"
            />
          </v-flex>
        </div>

        <v-layout column pa-2>
          <v-flex>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-text-field
                  :label="$t('pathForm.name') + ' *'"
                  v-model="name[langEdition]"
                  v-on="on"
                  :disabled="isReadOnly"
                  :counter="maxCharName"
                  :rules="getNameRules()"
                  @blur="updateName"
                  :persistent-hint="showHint"
                  :hint="langEdition !== locale ? name[locale] : null"
                >
                  <language-translator
                    v-if="locale !== langEdition"
                    :langEdition="langEdition"
                    :textToTranslate="name[locale]"
                    :type="'name'"
                    @text-translated="updateTranslatedText"
                    slot="append"
                  >
                  </language-translator>
                </v-text-field>
              </template>
              <span color="primary">{{ $t('pathForm.tooltips.name') }}</span>
            </v-tooltip>
          </v-flex>

          <v-flex>
            <!--v-tooltip top>
                <template v-slot:activator="{ on }"-->
            <v-menu
              :close-on-content-click="true"
              :offset-y="true"
              :min-width="100"
              :position-y="100"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :label="$t('pathForm.duration') + ' *'"
                  v-model="currentDurationTitle"
                  v-on="on"
                  v-bind="attrs"
                  readonly
                  :disabled="isReadOnly"
                ></v-text-field>
              </template>
              <v-list>
                <v-list-tile
                  v-for="duration in listDurations"
                  :key="duration.number"
                  @click="updateDuration(duration)"
                  align="center"
                  justify="center"
                >
                  <v-flex>
                    {{ duration.title }}
                  </v-flex>
                </v-list-tile>
              </v-list>
            </v-menu>
            <!--span color="primary">{{ $t('pathForm.tooltips.duration')}}</span>
              </v-tooltip-->
          </v-flex>

          <v-flex>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-textarea
                  :label="$t('pathForm.summary') + ' *'"
                  v-model="summary[langEdition]"
                  v-on="on"
                  :disabled="isReadOnly"
                  :counter="maxCharSummary"
                  :rules="getSummaryRules()"
                  no-resize
                  rows="3"
                  row-height="10"
                  @blur="updateSummary"
                  :persistent-hint="showHint"
                  :hint="langEdition !== locale ? summary[locale] : null"
                >
                  <language-translator
                    v-if="locale !== langEdition"
                    :langEdition="langEdition"
                    :textToTranslate="summary[locale]"
                    :type="'summary'"
                    @text-translated="updateTranslatedText"
                    slot="append"
                  >
                  </language-translator>
                </v-textarea>
              </template>
              <span color="primary">{{ $t('pathForm.tooltips.summary') }}</span>
            </v-tooltip>
          </v-flex>

          <v-flex pr-4>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                  outline
                  block
                  color="primary"
                  class="text-none"
                  v-on="on"
                  :disabled="isReadOnly"
                  @click="isLocationDialogOpen = true"
                >
                  <v-icon left :color="geolocation ? 'primary' : undefined"
                    >mdi-map</v-icon
                  >
                  <span v-if="geolocation">{{
                    $t('pathForm.changeLocation')
                  }}</span>
                  <span v-else> {{ $t('pathForm.chooseLocation') }}</span>
                </v-btn>
              </template>
              <span color="primary">{{
                $t('pathForm.tooltips.location')
              }}</span>
            </v-tooltip>

            <v-dialog v-model="isLocationDialogOpen" width="80%" lazy>
              <v-card>
                <v-flex
                  layout
                  justify-center
                  align-center
                  column
                  pl-4
                  pr-4
                  pb-2
                >
                  <v-card-title class="headline pb-1">
                    {{ $t('pathForm.locationLabel') }}
                  </v-card-title>
                  <!--span style="line-height: 120%" class="grey--text">
                    {{ $t('pathForm.locationSubtitle') }}
                  </span-->
                </v-flex>
                <v-layout justify-center>
                  <location-picker
                    :defaultLocation="geolocation"
                    :defaultRadius="pathInformations.radius || 100"
                    :color="'orange'"
                    :zoomOnClick="16"
                    :parent="'pathForm'"
                    @submit="updateLocation"
                    @cancel="cancelLocationSubmit"
                  />
                </v-layout>
              </v-card>
            </v-dialog>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-form>
  </v-card>
</template>

<script>
import ImageInput from './ImageInput';
import LocationPicker from './LocationPicker';
import LanguageTranslator from '../components/LanguageTranslator.vue';

export default {
  name: 'PathForm',
  components: {
    ImageInput,
    LocationPicker,
    LanguageTranslator,
  },
  props: {
    pathId: {
      type: String,
      default: null,
    },
    langEdition: {
      type: String,
      default: null,
    },
    showHint: {
      type: Boolean,
      default: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  created() {
    this.setPathValues();
  },
  data() {
    return {
      name: { fr: '', en: '' },
      duration: '',
      summary: { fr: '', en: '' },
      image: { author: '', legend: { fr: '', en: '' }, url: '' },
      // imageAuthor: '',
      // imageLegend: '',
      restriction: '',
      restrictionValue: '',
      geolocation: null,
      showPassword: false,
      isQrcodeDialogOpen: false,
      isLocationDialogOpen: false,
      locationBtnIcon: 'mdi-map-marker',
      locationBtnClass: '',
      passwordRules: [
        (password) =>
          Boolean(password) || this.$t('login.errors.passwordRequired'),
      ],
      firstPickerError: null,
      secondPickerError: null,
      isChecked: false,
      isValidPathForm: false,
      maxCharName: 50,
      maxCharSummary: 400,
    };
  },
  computed: {
    pathInformations() {
      return this.$store.getters['paths/getPathById'](this.pathId);
    },
    isPasswordRestriction() {
      return this.restriction === 'password';
    },
    isQrcodeRestriction() {
      return this.restriction === 'qrcode';
    },
    isGeolocRestriction() {
      return this.restriction === 'geolocalisation';
    },
    validPathInformation() {
      if (!this.image.url || !this.isValidPathForm || !this.geolocation) {
        return false;
      }
      return true;
    },
    currentDurationTitle() {
      if (!this.duration) {
        return null;
      }
      let currentDuration = this.listDurations.filter(
        (duration) => duration.number === this.duration
      );
      if (currentDuration.length === 0) {
        return null;
      }
      return currentDuration[0].title;
    },
    listDurations() {
      return [
        {
          title: '15 min',
          number: 15,
        },
        {
          title: '30 min',
          number: 30,
        },
        {
          title: '45 min',
          number: 45,
        },
        {
          title: '1 ' + this.$t('pathForm.length.hour'),
          number: 60,
        },
        {
          title: '1 ' + this.$t('pathForm.length.hour') + ' 30',
          number: 90,
        },
        {
          title: '2 ' + this.$t('pathForm.length.hours'),
          number: 120,
        },
      ];
    },
    locale() {
      return this.$i18n.locale;
    },
  },
  methods: {
    setPathValues() {
      this.pathInformations.langsEdited.forEach((lang) => {
        this.name[lang] = this.pathInformations.name[lang];
        this.summary[lang] = this.pathInformations.description[lang];
      });
      this.duration = this.pathInformations.duration;
      if (this.pathInformations.pictures) {
        this.image = this.pathInformations.pictures[0];
      }
      //  && this.pathInformations.pictures[0] && this.pathInformations.pictures[0].url;
      this.geolocation = this.pathInformations.geolocation;
    },
    getQrcodeUrl() {
      // prettier-ignore
      return this.restrictionValue.length
        ? `https://api.qrserver.com/v1/create-qr-code/?size=450x450&data=${this.restrictionValue}`
        : '';
    },
    getRequiredRule() {
      return [(data) => Boolean(data) || this.$t('pathForm.errors.required')];
    },
    getNameRules() {
      return [
        ...this.getRequiredRule(),
        (name) =>
          name.length <= this.maxCharName ||
          this.$tc('pathForm.errors.nameTooLong', this.maxCharName),
      ];
    },
    getSummaryRules() {
      return [
        ...this.getRequiredRule(),
        (summary) =>
          summary.length <= this.maxCharSummary ||
          this.$tc('pathForm.errors.summaryTooLong', this.maxCharSummary),
      ];
    },
    cancelLocationSubmit() {
      this.isLocationDialogOpen = false;
    },
    checkLocation() {
      this.locationBtnClass = '';
      if (!this.geolocation) {
        this.locationBtnClass = 'error';
      }
    },
    resetRestriction() {
      this.restrictionValue = '';
      this.restrictionGeolocation = null;
    },
    async updateName() {
      if (
        this.name &&
        (this.name[this.langEdition].length > this.maxCharName ||
          (this.pathInformations &&
            this.pathInformations.name &&
            this.pathInformations.name[this.langEdition] &&
            this.pathInformations.name[this.langEdition] ===
              this.name[this.langEdition]))
      ) {
        return;
      }
      const path = {
        name: { [this.langEdition]: this.name[this.langEdition] },
      };
      await this.updatePath(path);
    },
    async updateImage(value) {
      if (this.imageError) {
        this.imageError = null;
      }
      const path = { pictures: [value] };
      await this.updatePath(path);
    },
    async updateDuration(duration) {
      this.duration = duration.number;
      if (this.duration !== this.pathInformations.duration) {
        const path = { duration: this.duration };
        await this.updatePath(path);
      }
    },
    async updateSummary() {
      if (
        this.summary &&
        (this.summary[this.langEdition].length > this.maxCharSummary ||
          (this.pathInformations &&
            this.pathInformations.description &&
            this.pathInformations.description[this.langEdition] &&
            this.pathInformations.description[this.langEdition] ===
              this.summary[this.langEdition]))
      ) {
        return;
      }
      const path = {
        description: { [this.langEdition]: this.summary[this.langEdition] },
      };
      await this.updatePath(path);
    },
    async updateLocation(site) {
      this.geolocation = {
        type: 'point',
        coordinates: [site.latlng.lng, site.latlng.lat],
      };

      const path = { geolocation: this.geolocation, radius:site.radius };

      await this.updatePath(path);

      this.isLocationDialogOpen = false;
      this.locationBtnClass = '';
    },
    async updatePath(pathPayload) {
      this.$store.commit('app/SET_LOADING');
      try {
        await this.$store.dispatch('paths/UPDATE_PATH', {
          pathPayload: pathPayload,
          index: 'clients',
          pathId: this.pathId,
          kuzzle: this.$kuzzle,
        });
      } catch (e) {
        console.log(e);
        this.$toast.show(
          this.$t('pathManagement.errors.updatePath'),
          this.$t('common.error')
        );
      }
      this.$store.commit('app/UNSET_LOADING');
    },
    async updateTranslatedText(textTranslated, type) {
      if (textTranslated) {
        if (type === 'name') {
          this.name[this.langEdition] = textTranslated;
          this.updateName();
        } else if (type === 'summary') {
          this.summary[this.langEdition] = textTranslated;
          this.updateSummary();
        }
      }
    },
  },
  watch: {
    pathId(newValue) {
      return newValue;
    },
    pathInformations(newValue) {
      this.setPathValues();
      return newValue;
    },
    geolocation(geoloc) {
      if (geoloc === null) {
        this.locationBtnText = this.$t('pathForm.chooseLocation');
        this.locationBtnIcon = 'mdi-map-marker';
        return;
      }
      this.locationBtnText = this.$t('pathForm.changeLocation');
      this.locationBtnIcon = 'mdi-check';
    },
    isChecked() {
      if (this.isChecked) {
        this.restriction = 'qrcode';
      } else {
        this.restriction = '';
        this.resetRestriction();
      }
    },
    isValidPathForm(newvalue) {
      return newvalue;
    },
    image(newValue) {
      return newValue;
    },
    geolocation(newValue) {
      return newValue;
    },
    validPathInformation(newValue) {
      this.$emit('is-valid-path-form', newValue);
      return newValue;
    },
    langEdition(newValue) {
      this.$refs.pathForm.resetValidation();
      return newValue;
    },
  },
};
</script>

<style lang="sass">
@import '../assets/_vars.scss'
.btnWidth
  width: 215px
.layoutHeight
  height: 90vh
.formWidth
  width: 800px
.PathForm__restrictions .v-input--selection-controls__input
  display: none !important
.PathForm__restrictIcon
  width: 100px !important;
  height: 100px !important;
  border: 1px solid $secondary
  border-radius: 4px
  display: flex
  justify-content: center
  align-items: center
  margin: 1em 2em 1em 2em
  i
    color: $secondary !important
.PathForm__restrictIcon--selected
  background: $primary
.PathForm__label
  color: #aaa !important
.PathForm__key
  color: $secondary
  padding-left: 0.4em
  text-align: center
.PathForm__datePicker .v-text-field__details
  display: none
.PathForm__datePresets
  span
    color: $primary
  > *
    position: relative
    top: -3px
.theme--light.v-input:not(.v-input--is-disabled) input, .theme--light.v-input:not(.v-input--is-disabled) textarea
  font-size: 14px;
.missionImage
  max-height: 200px
  min-height: 200px
  max-width: 170px
  min-width: 170px
</style>
